<template>
    <div
        :style="`height:${height}px; width:${width}px`"
    >
        <LineChartJs
            :data="chartData"
            :options="computedOptions"
            :height="height"
            :width="width"
        />
    </div>
</template>

<script>
import {Line as LineChartJs} from 'vue-chartjs';

export default {
    name: 'LineChart',
    components: {LineChartJs},
    props: {
        chartData: {
            type: Object,
            required: true,
        },
        xAxesLabelString: {
            type: String,
            required: true,
        },
        yAxesLabelString: {
            type: String,
            required: true,
        },
        height: {
            type: Number,
            default: 350,
        },
        width: {
            type: Number,
            default: 350,
        },
        showLegend: {
            type: Boolean,
            default: false,
        },
        integerOnly: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        computedOptions() {
            const yAxesList = this.yAxesLabelString.split('<br>');
            // precision: 0 erzwingt ganzzahlige y-Tick-Werte für "Anzahl …"-Kennzahlen.
            // Bei Prozentwerten (0..1) wäre das falsch — daher dort ausgeklammert.
            const isPercent = this.chartData.datasets[0]?.isPercentValue;
            const integerTicks = this.integerOnly && !isPercent;
            const yAxisPrimary = {
                type: 'linear',
                position: 'left',
                min: 0,
                suggestedMax: this.chartData.datasets[0]?.maxValue ?? undefined,
                ticks: {
                    ...(integerTicks ? {precision: 0} : {}),
                    beginAtZero: true,
                    callback: (value) => {
                        const dataset = this.chartData.datasets[0];
                        if (dataset?.isPercentValue) {
                            return `${(value * 100).toFixed(0)} %`;
                        }
                        return value.toString().replace('.', ',');
                    },
                    font: {
                        family: "'Source Sans Pro'",
                        size: 13,
                    },
                },
                title: {
                    display: true,
                    text: yAxesList.length === 2 ? yAxesList[1] : yAxesList[0],
                    font: {
                        family: "'Source Sans Pro'",
                        size: 13,
                    },
                },
            };

            const yAxisSecondary =
                yAxesList.length === 2
                    ? {
                        type: 'linear',
                        position: 'left',
                        min: 0,
                        display: true,
                        grid: {
                            display: false,
                            color: 'transparent',
                        },
                        ticks: {
                            display: false,
                        },
                        title: {
                            display: true,
                            text: yAxesList[0],
                            font: {
                                family: "'Source Sans Pro'",
                                size: 13,
                            },
                        },
                    }
                    : undefined;

            return {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: {
                        display: this.showLegend,
                        position: 'top',
                        labels: {
                            font: {
                                family: "'Source Sans Pro'",
                                size: 13,
                            },
                            boxWidth: 8,
                            usePointStyle: true,
                            // Wenn das Dataset ein `kuerzel` mitliefert (Kennzahl 2.2.:
                            // Bundesland), zeigt die Legende das kompakte Kürzel, während
                            // der Tooltip weiterhin dataset.label (Vollname) nutzt.
                            generateLabels: (chart) => {
                                return chart.data.datasets.map((dataset, i) => ({
                                    text: dataset.kuerzel ?? dataset.label,
                                    fillStyle: dataset.borderColor,
                                    strokeStyle: dataset.borderColor,
                                    lineWidth: 0,
                                    hidden: !chart.isDatasetVisible(i),
                                    datasetIndex: i,
                                    pointStyle: dataset.pointStyle ?? 'circle',
                                }));
                            },
                        },
                    },
                    tooltip: {
                        callbacks: {
                            title: (tooltipItems) => {
                                const item = tooltipItems[0];
                                const xVal = item.raw?.x ?? item.label;
                                return xVal;
                            },
                            label: (tooltipItem) => {
                                const dataset = tooltipItem.dataset;
                                let label = dataset.label || '';
                                if (label) label += ': ';
                                const y = tooltipItem.raw?.y ?? tooltipItem.parsed.y;
                                const formatted = dataset.isPercentValue
                                    ? `${(y * 100).toFixed(0)} %`
                                    : y.toString().replace('.', ',');
                                return label + formatted;
                            },
                        },
                    },
                },
                scales: {
                    x: {
                        title: {
                            display: true,
                            text: this.xAxesLabelString,
                            font: {
                                family: "'Source Sans Pro'",
                                size: 13,
                            },
                        },
                        ticks: {
                            font: {
                                family: "'Source Sans Pro'",
                                size: 13,
                            },
                        },
                    },
                    y: yAxisPrimary,
                    ...(yAxisSecondary ? {y1: yAxisSecondary} : {}),
                },
            };
        },
    },
};
</script>

<style scoped>
</style>
